import React, { useState, useEffect, useContext } from "react";
import { Outlet, Link, NavLink, useLocation } from "react-router-dom";
import { useAuthState } from "react-redux-django-auth";
import {
  Menu,
  X,
  ArrowRight,
  LayoutDashboard,
  LogIn,
  Info,
  Layers,
  FolderGit2,
  GraduationCap,
  BookOpen,
  Mail,
} from "lucide-react";
import {
  CompanyInfoContext,
  ServiceContext,
  CourseContext,
  JobContext,
  BlogContext,
} from "../../App";
import Button from "../common/Button";
import Brand from "../common/Brand";

function NavBar() {
  const companyInfo = useContext(CompanyInfoContext) || {};
  const servicesContext = useContext(ServiceContext) || {};
  const courseContext = useContext(CourseContext) || {};
  const worksContext = useContext(JobContext) || {};
  const blogContext = useContext(BlogContext) || {};

  const { user, isAuthenticated } = useAuthState();
  const location = useLocation();

  // Scroll state with optimized event listener & passive listener
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 15);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Responsive Mobile Drawer state
  const [isMobileOpen, setMobileOpen] = useState(false);

  // Prevent background scrolling when mobile drawer is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  // Close mobile drawer on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close drawer upon navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Safe checks for context availability
  const hasServices = Array.isArray(servicesContext)
    ? servicesContext.length > 0
    : Array.isArray(servicesContext?.service)
      ? servicesContext.service.length > 0
      : Object.keys(servicesContext || {}).length > 0;

  const hasCourses = Array.isArray(courseContext)
    ? courseContext.length > 0
    : Array.isArray(courseContext?.courses)
      ? courseContext.courses.length > 0
      : Object.keys(courseContext || {}).length > 0;

  const hasWorks = Array.isArray(worksContext)
    ? worksContext.length > 0
    : Array.isArray(worksContext?.jobs)
      ? worksContext.jobs.length > 0
      : Object.keys(worksContext || {}).length > 0;

  const hasBlog = Array.isArray(blogContext)
    ? blogContext.length > 0
    : Array.isArray(blogContext?.blog)
      ? blogContext.blog.length > 0
      : Object.keys(blogContext || {}).length > 0;

  // Nav navigation items
  const navLinks = [
    { name: "About", to: "/about", show: true, icon: <Info size={16} /> },
    {
      name: "Services",
      to: "/services",
      show: hasServices,
      icon: <Layers size={16} />,
    },
    {
      name: "Portfolio",
      to: "/recent-jobs",
      show: hasWorks,
      icon: <FolderGit2 size={16} />,
    },
    {
      name: "Courses",
      to: "/courses",
      show: hasCourses,
      icon: <GraduationCap size={16} />,
    },
    {
      name: "Blogs",
      to: "/blog",
      show: hasBlog,
      icon: <BookOpen size={16} />,
    },
    {
      name: "Contact",
      to: "/contact",
      show: true,
      icon: <Mail size={16} />,
    },
  ];

  return (
    <>
      {/* ── Glassmorphism Top Navigation Bar (Fixed/Sticky to Top) ── */}
      <header
        className={`cht-fixed cht-top-0 cht-left-0 cht-right-0 cht-z-50 cht-w-full cht-transition-all cht-duration-300 ${
          scrolled
            ? "cht-bg-white/85 cht-backdrop-blur-xl cht-shadow-[0_4px_25px_rgba(0,0,0,0.06)] cht-border-b cht-border-gray-200/70"
            : "cht-bg-white/75 cht-backdrop-blur-xl cht-border-b cht-border-gray-200/50 cht-shadow-none"
        }`}
      >
        <div className="cht-max-w-7xl cht-mx-auto cht-px-4 sm:cht-px-6 lg:cht-px-8 cht-h-16 md:cht-h-20 cht-flex cht-items-center cht-justify-between">
          {/* Brand Logo & Name */}
          <Brand to="/" />

          {/* Desktop Navigation Link Pills */}
          <nav className="cht-hidden lg:cht-flex cht-items-center cht-gap-1.5 cht-bg-white/50 cht-backdrop-blur-md cht-px-2.5 cht-py-1 cht-rounded-2xl cht-border cht-border-gray-200/40 cht-shadow-none">
            {navLinks
              .filter((item) => item.show)
              .map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `cht-px-3.5 cht-py-2 cht-rounded-xl cht-text-xs md:cht-text-sm cht-font-semibold cht-transition-all cht-duration-200 cht-no-underline ${
                      isActive
                        ? "cht-text-[#3e57da] cht-bg-[#3e57da]/10 cht-font-bold"
                        : "cht-text-gray-600 hover:cht-text-[#3e57da] hover:cht-bg-gray-100/60"
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              ))}
          </nav>

          {/* Action CTAs & Auth Controls (Desktop only; hidden when hamburger menu is visible) */}
          <div className="cht-flex cht-items-center cht-gap-2 sm:cht-gap-3">
            <div className="cht-hidden lg:cht-flex cht-items-center cht-gap-2.5">
              {isAuthenticated ? (
                <Button
                  to="/dashboard"
                  variant="primary"
                  size="sm"
                  leftIcon={<LayoutDashboard size={14} />}
                >
                  <span className="cht-max-w-[120px] cht-truncate">
                    {user?.first_name ? `${user.first_name}` : "Dashboard"}
                  </span>
                </Button>
              ) : (
                <>
                  <Button
                    to="/login"
                    variant="ghost"
                    size="sm"
                    leftIcon={<LogIn size={14} />}
                  >
                    Sign In
                  </Button>

                  <Button
                    to="/signup"
                    variant="primary"
                    size="sm"
                    rightIcon={<ArrowRight size={13} />}
                  >
                    Start a Career
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Hamburger Menu Toggle */}
            <button
              type="button"
              onClick={() => setMobileOpen(!isMobileOpen)}
              className="lg:cht-hidden cht-flex cht-items-center cht-justify-center cht-w-9 cht-h-9 cht-rounded-xl cht-text-gray-600 hover:cht-text-[#3e57da] hover:cht-bg-gray-100/80 cht-border cht-border-gray-200/60 cht-transition-all active:cht-scale-95 cht-cursor-pointer"
              aria-label="Toggle navigation menu"
            >
              {isMobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Spacer to preserve natural flow under fixed navbar */}
      <div className="cht-h-16 md:cht-h-20" aria-hidden="true" />

      {/* ── Mobile Glass Slide-Over Drawer ── */}
      {isMobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="lg:cht-hidden cht-fixed cht-inset-0 cht-z-[110] cht-bg-black/30 cht-backdrop-blur-sm cht-transition-opacity cht-duration-300"
        />
      )}

      <aside
        className={`lg:cht-hidden cht-fixed cht-top-0 cht-right-0 cht-bottom-0 cht-z-[120] cht-w-[280px] sm:cht-w-[320px] cht-bg-white/95 cht-backdrop-blur-2xl cht-border-l cht-border-gray-200/70 cht-shadow-[0_10px_40px_rgba(0,0,0,0.15)] cht-flex cht-flex-col cht-justify-between cht-p-5 cht-transition-transform cht-duration-300 cht-ease-spring ${
          isMobileOpen ? "cht-translate-x-0" : "cht-translate-x-full"
        }`}
      >
        {/* Drawer Header */}
        <div className="cht-flex cht-items-center cht-justify-between cht-pb-4 cht-border-b cht-border-gray-100">
          <Brand to="/" onClick={() => setMobileOpen(false)} />
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="cht-p-1.5 cht-rounded-lg cht-text-gray-400 hover:cht-text-gray-700 hover:cht-bg-gray-100 cht-transition-colors cht-cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation Links inside Drawer */}
        <nav className="cht-flex-1 cht-py-4 cht-flex cht-flex-col cht-gap-1 cht-overflow-y-auto">
          {navLinks
            .filter((item) => item.show)
            .map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `cht-flex cht-items-center cht-gap-3 cht-px-3.5 cht-py-2.5 cht-rounded-xl cht-text-sm cht-font-semibold cht-transition-all cht-no-underline ${
                    isActive
                      ? "cht-text-[#3e57da] cht-bg-[#3e57da]/10 cht-font-bold"
                      : "cht-text-gray-700 hover:cht-text-[#3e57da] hover:cht-bg-gray-100/70"
                  }`
                }
              >
                {link.icon}
                <span>{link.name}</span>
              </NavLink>
            ))}
        </nav>

        {/* Drawer Action CTAs */}
        <div className="cht-pt-4 cht-border-t cht-border-gray-100 cht-flex cht-flex-col cht-gap-2.5">
          {isAuthenticated ? (
            <Button
              to="/dashboard"
              variant="primary"
              size="sm"
              fullWidth
              onClick={() => setMobileOpen(false)}
              leftIcon={<LayoutDashboard size={14} />}
            >
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button
                to="/signup"
                variant="primary"
                size="sm"
                fullWidth
                onClick={() => setMobileOpen(false)}
                rightIcon={<ArrowRight size={14} />}
              >
                Start a Career
              </Button>

              <Button
                to="/login"
                variant="subtle"
                size="sm"
                fullWidth
                onClick={() => setMobileOpen(false)}
                leftIcon={<LogIn size={14} />}
              >
                Sign In
              </Button>
            </>
          )}
        </div>
      </aside>

      {/* Main Page Outlet Section */}
      <section style={{ minHeight: "70vh" }}>
        <Outlet />
      </section>
    </>
  );
}

export default NavBar;
